import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import {
  DayButton,
  DayPicker,
  getDefaultClassNames,
  DropdownProps,
} from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

function CustomDropdown(props: DropdownProps) {
  const { value, onChange, options, className } = props;

  const handleChange = (newValue: string) => {
    const changeEvent = {
      target: { value: newValue },
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange?.(changeEvent);
  };

  const isYearDropdown = className?.includes('years');
  const maxWidth = isYearDropdown ? 'tw-max-w-[80px]' : 'tw-max-w-[70px]';

  return (
    <Select value={value?.toString()} onValueChange={handleChange}>
      <SelectTrigger
        className={cn(
          'tw-h-8 tw-text-sm tw-font-medium tw-border-input tw-w-auto tw-min-w-[60px] tw-justify-start tw-gap-1 tw-px-2',
          maxWidth,
        )}
        size="sm"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent
        position="popper"
        className="tw-z-[10000] tw-max-h-[300px]"
      >
        {options?.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value.toString()}
            className="tw-text-sm"
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  formatters,
  components,
  minDate,
  maxDate,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
  minDate?: Date;
  maxDate?: Date;
}) {
  const defaultClassNames = getDefaultClassNames();

  const dateDisabled = React.useMemo(() => {
    const disabledRules: any[] = [];
    if (minDate) {
      disabledRules.push({ before: minDate });
    }
    if (maxDate) {
      disabledRules.push({ after: maxDate });
    }

    if (disabledRules.length === 0) return props.disabled;
    if (props.disabled) {
      return [props.disabled, ...disabledRules];
    }
    return disabledRules;
  }, [minDate, maxDate, props.disabled]);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'tw-bg-background tw-group/calendar tw-p-3 tw-[--cell-size:--spacing(8)] tw-[[data-slot=card-content]_&]:bg-transparent tw-[[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('tw-w-auto', defaultClassNames.root),
        months: cn(
          'tw-flex tw-gap-4 tw-flex-col tw-md:flex-row tw-relative',
          defaultClassNames.months,
        ),
        month: cn(
          'tw-flex tw-flex-col tw-w-full tw-gap-4',
          defaultClassNames.month,
        ),
        nav: cn(
          'tw-flex tw-items-center tw-gap-1 tw-w-full tw-absolute tw-top-0 tw-inset-x-0 tw-justify-between tw-pointer-events-none',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          '!tw-w-8 !tw-h-8 tw-aria-disabled:opacity-50 tw-p-0 tw-select-none tw-pointer-events-auto tw-relative tw-z-10',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          '!tw-w-8 !tw-h-8 tw-aria-disabled:opacity-50 tw-p-0 tw-select-none tw-pointer-events-auto tw-relative tw-z-10',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'tw-flex tw-items-center tw-justify-center tw-h-(--cell-size) tw-w-full tw-px-(--cell-size) tw-relative',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'tw-w-full tw-flex tw-items-center tw-text-sm tw-font-medium tw-justify-center tw-gap-2 tw-px-1',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn('tw-relative', defaultClassNames.dropdown_root),
        dropdown: cn('tw-hidden', defaultClassNames.dropdown),
        caption_label: cn(
          'tw-select-none tw-font-medium',
          captionLayout === 'label'
            ? 'tw-text-sm'
            : 'tw-rounded-md tw-pl-2 tw-pr-1 tw-flex tw-items-center tw-gap-1 tw-text-sm tw-h-8 tw-[&>svg]:text-muted-foreground tw-[&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        // The weeks are flex rows, which the table layout algorithm cannot
        // size: the table box ends up shorter than its rows. Chrome hides
        // that by measuring the table as a flex item, WebKit (every browser
        // on iOS) does not and draws the footer over the last week. Laying
        // the grid out as blocks makes both engines size it the same.
        // (`table` was a v8 key; v9 calls this element `month_grid`.)
        month_grid: cn(
          'tw-block tw-w-full [&>thead]:tw-block',
          defaultClassNames.month_grid,
        ),
        weeks: cn('tw-block', defaultClassNames.weeks),
        weekdays: cn('tw-flex', defaultClassNames.weekdays),
        weekday: cn(
          'tw-text-muted-foreground tw-rounded-md tw-flex-1 tw-font-normal tw-text-[0.8rem] tw-select-none',
          defaultClassNames.weekday,
        ),
        week: cn('tw-flex tw-w-full tw-mt-2', defaultClassNames.week),
        week_number_header: cn(
          'tw-select-none tw-w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'tw-text-[0.8rem] tw-select-none tw-text-muted-foreground',
          defaultClassNames.week_number,
        ),
        day: cn(
          'tw-relative tw-w-full tw-h-full tw-p-0 tw-text-center tw-[&:first-child[data-selected=true]_button]:rounded-l-md tw-[&:last-child[data-selected=true]_button]:rounded-r-md tw-group/day tw-aspect-square tw-select-none',
          defaultClassNames.day,
        ),
        range_start: cn(
          'tw-rounded-l-md tw-bg-accent',
          defaultClassNames.range_start,
        ),
        range_middle: cn('tw-rounded-none', defaultClassNames.range_middle),
        range_end: cn(
          'tw-rounded-r-md tw-bg-accent',
          defaultClassNames.range_end,
        ),
        selected: cn(
          'tw-bg-primary tw-text-primary-foreground tw-rounded-md',
          defaultClassNames.selected,
        ),
        today: cn(
          'tw-bg-accent tw-text-accent-foreground tw-rounded-md tw-data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: cn(
          'tw-text-muted-foreground tw-aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'tw-text-muted-foreground tw-opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('tw-invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Dropdown: CustomDropdown,
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon
                className={cn('tw-size-4', className)}
                {...props}
              />
            );
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('tw-size-4', className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon
              className={cn('tw-size-4', className)}
              {...props}
            />
          );
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="tw-flex tw-size-(--cell-size) tw-items-center tw-justify-center tw-text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
      disabled={dateDisabled}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'tw-data-[selected-single=true]:bg-primary tw-data-[selected-single=true]:text-primary-foreground tw-data-[range-middle=true]:bg-accent tw-data-[range-middle=true]:text-accent-foreground tw-data-[range-start=true]:bg-primary tw-data-[range-start=true]:text-primary-foreground tw-data-[range-end=true]:bg-primary tw-data-[range-end=true]:text-primary-foreground tw-group-data-[focused=true]/day:border-ring tw-group-data-[focused=true]/day:ring-ring/50 tw-dark:hover:text-accent-foreground tw-flex tw-aspect-square tw-size-auto tw-w-full tw-min-w-(--cell-size) tw-flex-col tw-gap-1 tw-leading-none tw-font-normal tw-group-data-[focused=true]/day:relative tw-group-data-[focused=true]/day:z-10 tw-group-data-[focused=true]/day:ring-[3px] tw-data-[range-end=true]:rounded-md tw-data-[range-end=true]:rounded-r-md tw-data-[range-middle=true]:rounded-none tw-data-[range-start=true]:rounded-md tw-data-[range-start=true]:rounded-l-md tw-[&>span]:text-xs tw-[&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
